import React, { useState, useRef, useEffect } from "react";
import ReactQuillWrapper from "../../../../quillEditor";
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "../../../../../images/logo.png";
import IconButton from "../../../../iconButton";
import EmailTemplateTabs from "../../../../commons/EmailTemplateTabs";
import {
  useSaveTemplatesMutation,
  useUpdateTemplatesMutation,
} from "../../../../../store/templates";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { convertShortCodes, ehrTags } from "../../../../../utilities/utils";
import EmailTemplatePreview from "../../../../modal/EmailTemplatePreview";
import { useGetUserDetailsQuery } from "../../../../../store/admin/professionalUserInfo";
import { useAdminSaveProfessionalTemplatesMutation, useAdminUpdateProfessionalTemplatesMutation } from "../../../../../store/admin/adminTemplate";

const ProfessionalEmailTemplateEditor = () => {
  const isMobile =
    window.matchMedia &&
    window.matchMedia("only screen and (max-width: 767px)").matches;
  const location = useLocation();
  const textareaRef = useRef(null);
  const textareaRefMobile = useRef(null);
  const {
    mode,
    name,
    subject,
    content,
    add_photo_to_signature,
    templateId,
    professionalId,
  } = location?.state ?? {};
  const navigate = useNavigate();
  const [cursorPos, setCursorPos] = useState(0);
  const [selectedTab, setSelectedTab] = useState("Create Template");
  const [quillContent, setQuillContent] = useState(content ?? "");
  const [quillContentMobile, setQuillContentMobile] = useState(content ?? "");
  const [showEmailPreview, setShowEmailPreview] = useState(false);

  const { data: professionalInfo, refetch: professionalInfoDetail } =
    useGetUserDetailsQuery(professionalId);

  const [
    saveEmailTemplate,
    {
      isSuccess: saveEmailTemplateSuccess,
      isLoading: saveEmailTemplateLoader,
      isError: saveEmailTemplateError,
      data: saveEmailTemplateData,
    },
  ] = useAdminSaveProfessionalTemplatesMutation();

  const [
    updateEmailTemplate,
    {
      isSuccess: updateEmailTemplateSuccess,
      isLoading: updateEmailTemplateLoader,
      isError: updateEmailTemplateError,
      data: updateEmailTemplateData,
    },
  ] = useAdminUpdateProfessionalTemplatesMutation();

    useEffect(() => {
      if (saveEmailTemplateSuccess && !saveEmailTemplateLoader) {
        toast.success(saveEmailTemplateData.message);
        navigate("/dashboard/settings/email-template", {
          state: {
            updateEmailQuery: true,
          },
        });
      }

      if (updateEmailTemplateSuccess && !updateEmailTemplateLoader) {
        toast.success(updateEmailTemplateData.message);
        navigate("/dashboard/settings/email-template", {
          state: {
            updateEmailQuery: true,
          },
        });
      }
    }, [saveEmailTemplateSuccess, updateEmailTemplateSuccess]);

  const handleQuillChange = (content, delta, source, editor) => {
    formik.setFieldValue("templateContent", content);
    setQuillContent(content);
  };

  const handleQuillMobileChange = (content, delta, source, editor) => {
    formik.setFieldValue("templateContent", content);
    setQuillContentMobile(content);
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleTabChange = (tabName) => {
    setSelectedTab(tabName);
  };

  const validationSchema = Yup.object().shape({
    templateName: Yup.string().required("Template Name is required"),
    subject: Yup.string().required("Subject is required"),
    templateContent: Yup.string().required("Template Content is required"),
  });

  const formik = useFormik({
    initialValues: {
      templateName: name ?? "",
      subject: subject ?? "",
      templateContent: content ?? "",
      professionalName: "",
      photousername: add_photo_to_signature ?? 0,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      if (formik.isValid) {
        if (mode === "edit") {
          const params = {
            _method: "PUT",
            name: values.templateName,
            subject: values.subject,
            content: values.templateContent,
            add_photo_to_signature: values.photousername,
            id: templateId,
          };
          updateEmailTemplate(params);
        } else {
          const params = {
            type: "email",
            name: values.templateName,
            subject: values.subject,
            content: values.templateContent,
            add_photo_to_signature: values.photousername,
          };
          saveEmailTemplate(params);
        }
      }
    },
  });

  const handleTagClick = (tag) => {
    let tagName;

    if (tag === "+appt schedule link") {
      tagName = "schedule_link";
    } else {
      tagName = tag.toLowerCase().replace(/\s+/g, "_").replace(/^\+/, ""); // Remove the '+' symbol from the beginning of the tag
    }

    const tagWithBraces = `{{${tagName}}}`;
    if (parseInt(quillContent?.length + tagWithBraces?.length)) {
      setQuillContent((prevContent) => {
        const start = prevContent.substring(0, cursorPos + 3);
        const end = prevContent.substring(cursorPos + 3);
        setCursorPos(cursorPos + tagWithBraces?.length);
        return start + tagWithBraces + end;
      });
    }
  };

  const handleMobileTagClick = (tag) => {
    let tagName;

    if (tag === "+appt schedule link") {
      tagName = "schedule_link";
    } else {
      tagName = tag.toLowerCase().replace(/\s+/g, "_").replace(/^\+/, ""); // Remove the '+' symbol from the beginning of the tag
    }

    const tagWithBraces = `{{${tagName}}}`;
    if (parseInt(quillContent?.length + tagWithBraces?.length)) {
      setQuillContentMobile((prevContent) => {
        const start = prevContent.substring(0, cursorPos + 3);
        const end = prevContent.substring(cursorPos + 3);
        setCursorPos(cursorPos + tagWithBraces?.length);
        return start + tagWithBraces + end;
      });
    }
  };

  // useEffect(() => {
  //   if (
  //     textareaRef &&
  //     textareaRef.current &&
  //     textareaRef.current?.setSelectionRange
  //   ) {
  //     textareaRef && textareaRef.current?.focus();
  //     textareaRef &&
  //       textareaRef.current?.setSelectionRange(cursorPos, cursorPos);

  //     const lineHeight =
  //       parseInt(getComputedStyle(textareaRef.current).lineHeight, 10) || 20;
  //     const linesAboveCursor = cursorPos / textareaRef.current.cols - 5;
  //     textareaRef.current.scrollTop = lineHeight * linesAboveCursor;
  //   }
  // }, [cursorPos]);

  return (
    <div className="col-span-12">
      <div className="">
        <div className="flex items-center gap-[10px] mb-5">
          <div className="flex items-center gap-[10px]">
            <ArrowLeftIcon
              className="w-6 h-6 text-[#485868] cursor-pointer"
              onClick={handleGoBack}
            />
            <h3 className="text-xl font-normal leading-[130%] text-[#485868]">
              Settings
            </h3>
          </div>
          <span className="text-[#485868]">&#8226;</span>
          <h3 className="text-xl font-bold leading-[130%] text-customGray">
            InstantEngage™ Email Templates
          </h3>
        </div>
        <div className="block xl:hidden mb-5">
          <EmailTemplateTabs
            selectedTab={selectedTab}
            onSelectTab={handleTabChange}
          />
        </div>
        <div className="hidden xl:block">
          <div className="create-template">
            <div>
              <form>
                <input
                  type="text"
                  name="templateName"
                  placeholder="Template Name"
                  value={formik.values.templateName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="h-[60px] p-5 rounded-[10px] text-customGray border border-solid border-[#EBEFF3] text-base font-bold leading-[160%] placeholder:text-[#869099] mb-6"
                />
                {formik.touched.templateName && formik.errors.templateName && (
                  <p className="text-red-500 text-sm leading-[160%] font-bold mb-3 -mt-4 ml-1">
                    {formik.errors.templateName}
                  </p>
                )}
                <input
                  type="text"
                  name="subject"
                  placeholder="Subject"
                  value={formik.values.subject}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="h-[60px] p-5 rounded-[10px] text-customGray border border-solid border-[#EBEFF3] text-base font-bold leading-[160%] placeholder:text-[#869099] mb-6"
                />
                {formik.touched.subject && formik.errors.subject && (
                  <p className="text-red-500 text-sm leading-[160%] font-bold mb-3 -mt-4 ml-1">
                    {formik.errors.subject}
                  </p>
                )}

                <ReactQuillWrapper
                  quillContent={quillContent}
                  onChange={handleQuillChange}
                  textareaRef={textareaRef}
                  setCursorPos={setCursorPos}
                />
                {formik.touched.templateContent &&
                  formik.errors.templateContent && (
                    <p className="text-red-500 text-sm leading-[160%] font-bold mb-3 ml-1">
                      {formik.errors.templateContent}
                    </p>
                  )}
                <div className="flex flex-col md:flex-row gap-2 md:gap-[22px] mt-5 mb-7">
                  <label className="inline-block text-sm md:text-base font-bold leading-[160%] text-[#485868] max-w-[170px] w-full">
                    Insert a Dynamic Tag:
                  </label>
                  <div className="flex items-center flex-wrap gap-2.5 max-w-[calc(100%-200px)] w-full">
                    {ehrTags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 bg-[#F1F4F6] border border-solid border-[#EBEFF3] rounded-[40px] text-xs font-black leading-[160%] uppercase text-[#485868] inline-flex items-center justify-center h-8 cursor-pointer"
                        onClick={() => handleTagClick(tag)}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                {/* <div className="mt-6">
                  <input
                    type="text"
                    name="professionalName"
                    placeholder="Name Field"
                    value={formik.values.professionalName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="h-[60px] p-5 rounded-[10px] border border-solid border-[#EBEFF3] text-base font-bold leading-[160%] text-customGray placeholder:text-base placeholder:text-[#869099] placeholder:font-bold mb-6"
                  />
                </div> */}
                {/* <div className="form-group flex items-center mb-[30px]">
                  <div className="flex items-center relative">
                    <input
                      id="photousername"
                      aria-describedby="comments-description"
                      name="photousername"
                      type="checkbox"
                      checked={formik.values.photousername === 1}
                      onChange={(e) => {
                        formik.handleChange(e);
                        formik.setFieldValue(
                          "photousername",
                          e.target.checked ? 1 : 0
                        );
                      }}
                      className="rounded border border-solid border-[#CCD0D4] relative cursor-pointer inline-block w-5 h-5 leading-[0px]"
                    />
                  </div>
                  <div className="min-w-0 flex-1 text-sm leading-6">
                    <label
                      htmlFor="photousername"
                      className="font-medium text-customGray text-sm md:text-base leading-[160%] pl-[14px] cursor-pointer"
                    >
                      Include my photo in the signature
                    </label>
                  </div>
                </div> */}
                <div className="flex items-center gap-5">
                  <IconButton
                    classes="text-base font-black leading-[160%] text-white flex items-center justify-center gap-2.5 h-[58px] max-w-[200px] w-full bg-primary rounded-[10px] uppercase"
                    icon={CheckCircleIcon}
                    iconClasses="w-6 h-6 text-white"
                    onClick={formik.handleSubmit}
                    title="SAVE"
                    buttonLoader={
                      saveEmailTemplateLoader || updateEmailTemplateLoader
                    }
                  />
                  <IconButton
                    classes="text-base font-black leading-[160%] text-[#485868] flex items-center justify-center gap-2.5 h-[58px] max-w-[200px] w-full bg-[#EBEFF3] rounded-[10px] uppercase"
                    icon={XCircleIcon}
                    iconClasses="w-6 h-6 text-[#485868]"
                    onClick={handleGoBack}
                    title="CANCEL"
                  />
                  <IconButton
                    classes="text-base font-black leading-[160%] text-white flex items-center justify-center gap-2.5 h-[58px] max-w-[200px] w-full bg-primary rounded-[10px] uppercase"
                    // icon={XCircleIcon}
                    iconClasses="w-6 h-6 text-[#485868]"
                    onClick={() => {
                      setShowEmailPreview(true);
                    }}
                    title="preview"
                  />
                </div>
              </form>
            </div>
          </div>
          {/* <div className="xl:fixed right-0 top-[99px] xl:max-w-[400px] xxl:max-w-[478px] w-full h-full border border-solid border-[#EBEFF3] py-6 px-9 bg-white preview-template">
            <div className="border-b border-solid border-[#EBEFF3] text-center pb-6 mb-[30px]">
              <Link to="/">
                <img src={Logo} alt="" className="h-[34px]" />
              </Link>
            </div>
            <div id="test" dangerouslySetInnerHTML={{ __html: quillContent }} />
          </div> */}
        </div>
        <div className="block xl:hidden">
          {selectedTab === "Create Template" && (
            <div className="create-template">
              <div>
                <form>
                  <input
                    type="text"
                    name="templateName"
                    placeholder="Template Name"
                    value={formik.values.templateName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="h-12 p-[14px] rounded-[10px] border border-solid border-[#EBEFF3] text-sm font-bold leading-[160%] text-customGray placeholder:text-[#869099] mb-[14px]"
                  />
                  {formik.touched.templateName &&
                    formik.errors.templateName && (
                      <p className="text-red-500 text-xs leading-[160%] font-bold mb-3 -mt-4 ml-1">
                        {formik.errors.templateName}
                      </p>
                    )}
                  <input
                    type="text"
                    name="subject"
                    placeholder="Subject"
                    value={formik.values.subject}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="h-12 p-[14px] rounded-[10px] border border-solid border-[#EBEFF3] text-sm font-bold leading-[160%] text-customGray placeholder:text-[#869099] mb-[14px]"
                  />
                  {formik.touched.subject && formik.errors.subject && (
                    <p className="text-red-500 text-xs leading-[160%] font-bold mb-3 -mt-4 ml-1">
                      {formik.errors.subject}
                    </p>
                  )}
                </form>
                <ReactQuillWrapper
                  quillContent={quillContentMobile}
                  onChange={handleQuillMobileChange}
                  textareaRef={textareaRefMobile}
                  setCursorPos={setCursorPos}
                />
                <div className="flex flex-col md:flex-row gap-2 md:gap-[22px] mt-5 mb-7">
                  <label className="inline-block text-sm md:text-base font-bold leading-[160%] text-[#485868] md:max-w-[170px] w-full">
                    Insert a Dynamic Tag:
                  </label>
                  <div className="flex items-center flex-wrap gap-2.5 md:max-w-[calc(100%-200px)] w-full">
                    {ehrTags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 bg-[#F1F4F6] border border-solid border-[#EBEFF3] rounded-[40px] text-xs font-black leading-[160%] uppercase text-[#485868] inline-flex items-center justify-center h-8 cursor-pointer"
                        onClick={() => handleMobileTagClick(tag)}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                {/* <div className="mt-[14px]">
                  <input
                    type="text"
                    name="professionalName"
                    placeholder="Name Field"
                    value={formik.values.professionalName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="h-12 p-[14px] rounded-[10px] border border-solid border-[#EBEFF3] text-sm font-bold leading-[160%] text-customGray placeholder:text-[#869099] mb-6"
                  />
                </div> */}
                {/* <div className="form-group flex items-center mb-6">
                  <div className="flex items-center relative">
                    <input
                      id="photousername"
                      aria-describedby="comments-description"
                      name="photousername"
                      type="checkbox"
                      checked={formik.values.photousername === 1}
                      onChange={(e) => {
                        formik.handleChange(e);
                        formik.setFieldValue(
                          "photousername",
                          e.target.checked ? 1 : 0
                        );
                      }}
                      className="rounded border border-solid border-[#CCD0D4] relative cursor-pointer inline-block w-5 h-5 leading-[0px]"
                    />
                  </div>
                  <div className="min-w-0 flex-1 text-sm leading-6">
                    <label
                      htmlFor="photousername"
                      className="font-medium text-customGray text-sm leading-[160%] pl-[14px] cursor-pointer"
                    >
                      Include my photo in the signature
                    </label>
                  </div>
                </div> */}
                <div className="flex items-center gap-5">
                  <IconButton
                    classes="text-sm font-black leading-[160%] text-white flex items-center justify-center gap-2.5 h-10 max-w-[162px] w-full bg-primary rounded-[10px] uppercase"
                    icon={CheckCircleIcon}
                    iconClasses="w-6 h-6 text-white"
                    onClick={formik.handleSubmit}
                    title="SAVE"
                    buttonLoader={
                      saveEmailTemplateLoader || updateEmailTemplateLoader
                    }
                  />
                  <IconButton
                    classes="text-sm font-black leading-[160%] text-[#485868] flex items-center justify-center gap-2.5 h-10 max-w-[162px] w-full bg-[#EBEFF3] rounded-[10px] uppercase"
                    icon={XCircleIcon}
                    iconClasses="w-6 h-6 text-[#485868]"
                    title="CANCEL"
                  />
                </div>
              </div>
            </div>
          )}
          {selectedTab === "Preview Template" && (
            <div className="w-full h-full border border-solid border-[#EBEFF3] py-6 px-9 rounded-md bg-white preview-template min-h-[640px]">
              <div className="border-b border-solid border-[#EBEFF3] text-center pb-6 mb-[30px]">
                <Link to="/">
                  <img src={Logo} alt="" className="h-[34px]" />
                </Link>
              </div>
              <div
                id="test"
                dangerouslySetInnerHTML={{
                  __html: convertShortCodes(
                    quillContentMobile,
                    professionalInfo?.data
                  ),
                }}
              />
            </div>
          )}
        </div>
      </div>
      {showEmailPreview && (
        <EmailTemplatePreview
          showModal={showEmailPreview}
          setShowModal={setShowEmailPreview}
          content={convertShortCodes(quillContent, professionalInfo?.data)}
        />
      )}
    </div>
  );
};

export default ProfessionalEmailTemplateEditor;
